import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';

import { ContactModel } from '../../../../../Common/TargetingShared/model/contact.model';
import { OutletModel } from '../../../../../Discover/Targeting/models/outlet.model';
import { HandleTypeEnum } from '../../../../../api/targeting/enum/handle-type.enum';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { AddressModel, PrivateDataModel } from '../../../models/private-data.model';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { ContactsPublicApi } from '../../../../../api/targeting/contacts-public.api';
import { OutletsPublicApi } from '../../../../../api/targeting/outlets-public.api';
import { JournodexRegionsService } from '../../../../../Common/Journodex/services/journodex-regions.service';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { AddressApiModel } from '../../../../../api/targeting/model/private-data-api.model';
import { ContactService } from '../../../../../Common/TargetingShared/services/contact-service';
import { TargetingRhpContextsService } from '../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { UserModel } from '../../../../../Core/Users/models/user.model';
import { NotesChange } from '../../../../../Common/TargetingShared/components/UserNotes/user-notes.component';
import { JournodexEntityFactory } from '../../../factories/journodex-entity.factory';
import { HandleModel } from '../../../models/handle.model';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { AddPrivateDataDialogService } from './add-private-data-dialog.service';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';


@Component({
    selector: 'agpr-add-private-data-dialog',
    templateUrl: './add-private-data-dialog.component.html',
    styleUrls: ['./add-private-data-dialog.component.scss'],
    providers: [
        AddPrivateDataDialogService
    ],
    standalone: false
})
export class AddPrivateDataDialogComponent extends AbstractDialogComponent implements OnInit {
    @Input() set setEntity(entity: ContactModel | OutletModel) {
        this.systemEntity = entity.clone();
        if (entity instanceof ContactModel) {
            this.contactHasSystemAddresses = !!(entity.selectedEmployment?.outlet) && (!!entity.selectedEmployment.outlet.addressLine1
                || !!entity.selectedEmployment.outlet.addressLine2 || !!entity.selectedEmployment.outlet.geography
                || !!entity.selectedEmployment.outlet.postalCode);
        }
    }

    @Input() set setPrivateData(privateData: PrivateDataModel) {
        if (!privateData) {
            return;
        }

        this.cachedPrivateData = privateData.clone();
        this.privateData = privateData.clone();
    }

    @Input() currentUser: UserModel;
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @Output() privateDataUpdate: EventEmitter<any> = new EventEmitter<any>();

    hideSlideOverlay: boolean = false;
    loading: boolean = false;
    systemEntity: ContactModel | OutletModel;
    privateData: PrivateDataModel;
    cachedPrivateData: PrivateDataModel;
    focusedEntity: ContactModel | OutletModel;
    contactHasSystemAddresses: boolean = false;
    privateDataDirty: boolean = false;
    addressFieldsDirty: boolean = false;
    preferredEmail: HandleModel;
    isCachedPreferredEmailPrivate: boolean = false;

    readonly id = 'privateDataDialog';
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly entityType = EntityType;
    readonly handleTypeEnum = HandleTypeEnum;
    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        public coreDialogService: CoreDialogService,
        public contactsPublicApi: ContactsPublicApi,
        public outletsPublicApi: OutletsPublicApi,
        public contactService: ContactService,
        public messageService: MessageService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public journodexRegionsService: JournodexRegionsService,
        public localizationService: LocalizationService,
        private journodexEntityFactory: JournodexEntityFactory,
        public addPrivateDataDialogService: AddPrivateDataDialogService
    ) {
        super(coreDialogService, 'AddPrivateData');
    }

    ngOnInit() {
        this.focusedEntity = this.systemEntity?.entityType === EntityType.CONTACT
            ? this.journodexEntityFactory.createEmptyContact()
            : this.journodexEntityFactory.createEmptyOutlet();
        this.focusedEntity.handles = this.systemEntity?.handles?.map(x => {
            x.editable = false;
            return x;
        });
        this.privateData?.handles?.forEach(handle => {
            this.focusedEntity.handles.push(handle);
            if (handle.handleType === HandleTypeEnum.EMAIL && handle.preferred) {
                this.isCachedPreferredEmailPrivate = true;
            }
        });
    }

    addHandle(e) {
        e.isPrivateData = true;
        e.primary = false;
        this.privateData.handles.push(e);
        this.focusedEntity.handles.push(e);
        this.updateDirtyFlag();
    }

    removeHandle(e) {
        this.privateData.handles = this.privateData.handles.filter(x => !(x.handleType === e.handleType && x.value === e.value));
        this.focusedEntity.handles = this.focusedEntity.handles.filter(x => !(x.handleType === e.handleType && x.value === e.value));
        // if removing a private preferred email, reset preferred email to the primary one
        if (e.handleType === HandleTypeEnum.EMAIL && e.preferred) {
            this.preferredEmail = this.focusedEntity.handles.find(x => x.handleType === HandleTypeEnum.EMAIL && x.primary);
        }
        this.updateDirtyFlag();
    }

    addAddress() {
        this.privateData.addresses.push(new AddressModel({
            addressLine1: this.focusedEntity.addressLine1 || null,
            addressLine2: this.focusedEntity.addressLine2 || null,
            geography: this.focusedEntity.geography?.toApiEntity() || null,
            postalCode: this.focusedEntity.postalCode || null
        } as AddressApiModel));
        this.focusedEntity.addressLine1 = this.focusedEntity.addressLine2 = this.focusedEntity.geography = this.focusedEntity.postalCode = null;
        this.addressFieldsDirty = false;
        this.updateDirtyFlag();
    }

    removeAddress(address: AddressModel) {
        this.privateData.addresses = this.privateData.addresses.filter(x => !x.equals(address));
        this.updateDirtyFlag();
    }

    modifyNotes(notesChange: NotesChange) {
        const entity = this.systemEntity;
        this.targetingRhpContextsService.modifyNotes(notesChange?.notes, entity, this.privateData)
            .then(done => {
                this.privateData.notes = notesChange.notes;
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform(`Discover.userNotes.${notesChange.type}SuccessMessage`)
                });
                this.contactService.setNotesAllLayerUpdate(
                    { entityId: entity?.id, entityType: entity?.entityType, notes: notesChange?.notes }
                );
            })
            .catch(error => {
                this.privateData.notes = this.privateData.notes.slice();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform(`Discover.userNotes.${notesChange.type}ErrorMessage`)
                });
            });
        this.privateData.notes = notesChange.notes;
        this.updateDirtyFlag();
    }

    updateDirtyFlag() {
        this.privateDataDirty = !this.cachedPrivateData.equals(this.privateData);
    }

    updateAddressDirtyFlag() {
        this.addressFieldsDirty = !!this.focusedEntity.addressLine1 || !!this.focusedEntity.addressLine2
            || !!this.focusedEntity.geography || !!this.focusedEntity.postalCode;
    }

    setPreferredEmail(handle: HandleModel) {
        if (!(handle && (handle.primary || handle.preferred))) {
            return;
        }
        this.preferredEmail = handle;
    }

    updatePrivateData() {
        this.addPrivateDataDialogService.updatePrivateData(
            this.systemEntity, this.privateData, this.focusedEntity, this.preferredEmail, this.privateDataDirty, this.isCachedPreferredEmailPrivate
        ).subscribe(done => {
            this.close();
        });
    }

    close() {
        this.hideSlideOverlay = true;
        this.closeDialog();
    }
}
